import ErrorBoundary from '@components/Errors/ErrorBoundary';
import { MiniCartLoading } from '@components/MiniCart/MiniCartLoading';
import {
  MINI_CART_MICROCOPY_DEFAULTS,
  MINI_CART_MICROCOPY_SET,
} from '@components/MiniCart/miniCartMicrocopy';
import { CartContext, CartProvider } from '@contexts/CartContext';
import { analytics } from '@lib/analytics/page-events';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { notifyCartChanged } from '@lib/commerce/cartChanged';
import { subscribeToMiniCartOpenRequest } from '@lib/commerce/miniCartOpen';
import { useCartCount } from '@lib/commerce/useCartCount';
import { useMultiCart } from '@lib/flags/useMultiCart';
import { HeaderFastActionItem } from '@lib/types/cms/Header';
import * as Sentry from '@sentry/nextjs';
import { c } from '@ui-components/utils/tailwind-merge';
import dynamic from 'next/dynamic';
import {
  ForwardedRef,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, CartDrawer, EmptyState, IconWithCount } from 'ui-components';
import { IconMenuOption } from '../Header/IconMenuOption';

const loadMiniCartIsland = () => import('@components/MiniCart/MiniCartIsland');

/**
 * Client-only and fetched on the first open, so flag-off pages, and flag-on
 * pages nobody opens the cart on, ship none of commerce-ui, MUI or emotion.
 */
const MiniCartIsland = dynamic(
  () => loadMiniCartIsland().then(({ MiniCartIsland }) => MiniCartIsland),
  { ssr: false, loading: () => <MiniCartLoading /> }
);

/** Starts the chunk download on hover/focus, ahead of the click. */
const prefetchMiniCartIsland = () => {
  void loadMiniCartIsland().catch((error) => Sentry.captureException(error));
};

type FastActionMenuCartProps = {
  item: HeaderFastActionItem;
  forwardedRef: ForwardedRef<HTMLDivElement>;
  referenceProps: {};
  /** Merged onto the cart link's own classes. */
  className?: string;
};

type FastActionMenuCartLinkProps = FastActionMenuCartProps & {
  /** `undefined` hides the badge and leaves the count out of the label. */
  itemCount: number | undefined;
  /** Renders a button that opens the mini-cart instead of the cart link. */
  onClick?: () => void;
  isExpanded?: boolean;
};

const FastActionMenuCartLink = ({
  item,
  forwardedRef,
  referenceProps,
  className,
  itemCount,
  onClick,
  isExpanded,
}: FastActionMenuCartLinkProps) => {
  // TODO: Translate the text
  const cartAriaLabel =
    itemCount === undefined
      ? `View ${item.topLevelLink?.value}`
      : `View ${item.topLevelLink?.value} (${itemCount} item${itemCount === 1 ? '' : 's'})`;
  return (
    <IconMenuOption
      name={cartAriaLabel}
      href={item.topLevelLink?.url || ''}
      ref={forwardedRef}
      {...referenceProps}
      {...(onClick
        ? { 'aria-haspopup': 'dialog', 'aria-expanded': isExpanded }
        : {})}
      onClick={onClick}
      testId={onClick ? 'mini-cart-trigger' : undefined}
      className={c('relative rounded-full', className)}
      target={item.topLevelLink?.openInNewTab ? '_blank' : undefined}
    >
      <IconWithCount
        iconName={item.topLevelLink?.icon || ''}
        iconSize={20}
        count={itemCount ?? 0}
      />
    </IconMenuOption>
  );
};

/** Today's ASv2 count, unchanged while MULTI_CART is off. */
const Asv2FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { cart } = useContext(CartContext);
  return <FastActionMenuCartLink {...props} itemCount={cart?.ItemCount || 0} />;
};

/**
 * CPS-7694: read-only commerce cart count, hidden while rate limited.
 * CPS-7696: the icon opens the commerce-ui mini-cart in a drawer.
 * CPS-7695: so does a page's request, e.g. the PDP's View in cart CTA.
 */
const MultiCartFastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { count } = useCartCount();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  // What had focus when a page asked for the drawer, if that page wants focus
  // back there; otherwise focus goes to the cart button.
  const requesterRef = useRef<HTMLElement | null>(null);
  const mc = useMicrocopy(
    MINI_CART_MICROCOPY_SET,
    MINI_CART_MICROCOPY_DEFAULTS
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    analytics.pushMiniCartActionEvent({
      mini_cart_action: open ? 'open' : 'close',
    });
    // Whatever changed inside, the badge rereads once the drawer closes.
    if (!open) notifyCartChanged();
  }, []);

  useEffect(
    () =>
      subscribeToMiniCartOpenRequest(({ returnFocus }) => {
        requesterRef.current =
          returnFocus === 'requester' &&
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        handleOpenChange(true);
      }),
    [handleOpenChange]
  );

  // Next's lazy component caches a rejected chunk load across drawer opens.
  const reloadPage = () => window.location.reload();

  // The drawer is opened from state, not a Radix trigger, so hand focus back
  // ourselves: to the page control that asked for it, else the cart button.
  const returnFocusToTrigger = useCallback((event: Event) => {
    event.preventDefault();
    const requester = requesterRef.current;
    requesterRef.current = null;
    if (requester?.isConnected) {
      requester.focus();
      return;
    }
    wrapperRef.current?.querySelector('button')?.focus();
  }, []);

  return (
    <span
      ref={wrapperRef}
      onPointerEnter={prefetchMiniCartIsland}
      onFocusCapture={prefetchMiniCartIsland}
    >
      <FastActionMenuCartLink
        {...props}
        itemCount={count}
        onClick={() => handleOpenChange(true)}
        isExpanded={isOpen}
      />
      <CartDrawer
        open={isOpen}
        onOpenChange={handleOpenChange}
        onCloseAutoFocus={returnFocusToTrigger}
        microcopy={{
          title: mc['mini-cart.title'],
          close: mc['mini-cart.close'],
        }}
      >
        <ErrorBoundary
          fallback={
            <EmptyState
              microcopy={{
                title: mc['mini-cart.error-title'],
                description: mc['mini-cart.error-description'],
                cta: mc['mini-cart.reload'],
              }}
              onClick={reloadPage}
              modalTrigger={
                <Button label={mc['mini-cart.reload']} onClick={reloadPage} />
              }
            />
          }
        >
          <MiniCartIsland />
        </ErrorBoundary>
      </CartDrawer>
    </span>
  );
};

export const FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { enabled } = useMultiCart();
  return enabled ? (
    <MultiCartFastActionMenuCart {...props} />
  ) : (
    <Asv2FastActionMenuCart {...props} />
  );
};

/**
 * Mounts the legacy ASv2 CartProvider only while MULTI_CART is off: its
 * `/api/carts` read would otherwise still run (and mint a device for a
 * cookieless guest) even though the badge no longer reads it.
 */
export const FastActionMenuCartProvider = ({ children }: PropsWithChildren) => {
  const { enabled } = useMultiCart();
  return enabled ? <>{children}</> : <CartProvider>{children}</CartProvider>;
};
